import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { ALL_VALID_RANKS, ALL_SERVICE_BRANCH_VALUES } from '@/lib/military-constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import MilitaryFields from '@/components/registration/MilitaryFields';
import TechnologyAreaPicker from '@/components/registration/TechnologyAreaPicker';
import AcknowledgementSection from '@/components/registration/AcknowledgementSection';
import AthenaOptOutCheckbox from '@/components/registration/AthenaOptOutCheckbox';
import RegistrationConfirmation from '@/components/registration/RegistrationConfirmation';
import { ATTENDEE_PARTICIPANT_TYPES, ATTENDEE_ACKNOWLEDGEMENTS } from '@/lib/constants';
import { safeAttendeeParticipantType, safeBio, safeUrl } from '@/lib/registration-prefill';
import type { EventPublicInfo, EmailCheckResult } from '@/types';
import { formatEventDateRange } from '@/hooks/useActiveEvent';
import { normalizeUrl, isValidUrl } from '@/lib/utils';
import { getApiErrorMessage, getApiFieldErrors } from '@/lib/apiError';
import { Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  participantType: string;
  rank: string;
  branchOfService: string;
  isRetired: boolean;
  organization: string;
  role: string;
  linkedinUrl: string;
  websiteUrl: string;
  bio: string;
  accelerationInterests: string[];
  athenaOptOut: boolean;
  acknowledgements: string[];
};

const INITIAL_FORM: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  participantType: '',
  rank: '',
  branchOfService: '',
  isRetired: false,
  organization: '',
  role: '',
  linkedinUrl: '',
  websiteUrl: '',
  bio: '',
  accelerationInterests: [],
  athenaOptOut: false,
  acknowledgements: [],
};

export default function RegisterAttendeePage() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const { user, isAuthenticated } = useAuth();

  const [event, setEvent] = useState<EventPublicInfo | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState('');

  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Email check state
  const [emailCheckResult, setEmailCheckResult] = useState<EmailCheckResult | null>(null);
  const lastCheckedEmail = useRef('');

  // Sign-in modal state
  const [showSignIn, setShowSignIn] = useState(false);
  // "Already registered for this event" modal state
  const [showAlreadyRegistered, setShowAlreadyRegistered] = useState(false);
  // "Pending admin approval" modal state
  const [showPendingApproval, setShowPendingApproval] = useState(false);
  // "Registration rejected" modal state
  const [showRejected, setShowRejected] = useState(false);
  const [signInPassword, setSignInPassword] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  // ----- Event fetching -----

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const url = eventId ? `/events/${eventId}/public` : '/events/active/public';
      const res = await api.get<{ success: boolean; data: EventPublicInfo }>(url);
      if (res.success) {
        if (res.data.status !== 'active') {
          setEventError('Registration for this event is not currently open.');
        } else {
          setEvent(res.data);
        }
      }
    } catch {
      setEventError(
        eventId
          ? 'Event not found. Please check your registration link.'
          : 'No active event found. Please check back later.'
      );
    } finally {
      setLoadingEvent(false);
    }
  };

  // ----- Helpers -----

  const set = useCallback(
    (field: keyof FormData, value: string | string[] | boolean) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    [],
  );

  // ----- Email check on blur -----

  const handleEmailBlur = useCallback(async () => {
    const email = form.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (email === lastCheckedEmail.current) return;
    lastCheckedEmail.current = email;

    try {
      const res = await api.post<{ success: boolean; data: EmailCheckResult }>(
        '/register/check-email',
        { email, eventId: event?.id },
      );
      if (res.success && res.data.exists) {
        setEmailCheckResult(res.data);

        const isCurrentUser = isAuthenticated && user?.email?.toLowerCase() === email;

        if (res.data.isRejected) {
          setShowSignIn(false);
          setShowRejected(true);
        } else if (res.data.hasPendingRegistration) {
          // Registration submitted but awaiting admin approval
          setShowSignIn(false);
          setShowPendingApproval(true);
        } else if (res.data.hasEventRegistration) {
          // Already approved for this event — surface the "already registered" modal
          setShowSignIn(false);
          setShowAlreadyRegistered(true);
        } else if (res.data.hasAccount && !isCurrentUser) {
          // Different account or not signed in — require sign-in
          setShowSignIn(true);
        } else if (isCurrentUser && res.data.profile) {
          // Current user — safe to prefill from check-email profile
          const p = res.data.profile;
          const safeRank = p.rank && ALL_VALID_RANKS.includes(p.rank) ? p.rank : '';
          const safeBranch = p.branchOfService && ALL_SERVICE_BRANCH_VALUES.includes(p.branchOfService) ? p.branchOfService : '';
          const safePType = safeAttendeeParticipantType(p.participantType);
          const safeLinkedin = safeUrl(p.linkedinUrl);
          const safeWebsite = safeUrl(p.websiteUrl);
          const clampedBio = safeBio(p.bio);
          setForm((prev) => ({
            ...prev,
            firstName: p.firstName || prev.firstName,
            lastName: p.lastName || prev.lastName,
            organization: p.organization || prev.organization,
            role: p.role || prev.role,
            rank: safeRank || prev.rank,
            branchOfService: safeBranch || prev.branchOfService,
            isRetired: p.isRetired ?? prev.isRetired,
            participantType: safePType || prev.participantType,
            linkedinUrl: safeLinkedin || prev.linkedinUrl,
            websiteUrl: safeWebsite || prev.websiteUrl,
            bio: clampedBio || prev.bio,
            accelerationInterests:
              p.accelerationInterests?.length
                ? p.accelerationInterests.slice(0, 5)
                : prev.accelerationInterests,
          }));
        }
        // No account (prior registration only) — don't prefill without auth
      } else {
        setEmailCheckResult(null);
      }
    } catch {
      // Silent fail — pre-fill is a convenience, not critical
    }
  }, [form.email, event?.id, isAuthenticated, user?.email]);

  // ----- Sign-in for existing accounts -----

  const handleSignIn = async () => {
    const email = form.email.trim().toLowerCase();
    if (!signInPassword) {
      setSignInError('Password is required');
      return;
    }
    setSigningIn(true);
    setSignInError('');
    try {
      // Use registration-login endpoint — skips active-event gate so
      // returning users can authenticate before being approved for the new event
      const res = await api.post<{
        user: Record<string, unknown>;
        accessToken: string;
        expiresIn: number;
      }>('/auth/registration-login', { email, password: signInPassword });

      api.setAuthTokens({ accessToken: res.accessToken, expiresIn: res.expiresIn });
      setShowSignIn(false);
      setSignInPassword('');

      // Pre-fill form from the returned user profile
      const p = res.user;
      const safeRank = typeof p.rank === 'string' && ALL_VALID_RANKS.includes(p.rank) ? p.rank : '';
      const safeBranch = typeof p.branchOfService === 'string' && ALL_SERVICE_BRANCH_VALUES.includes(p.branchOfService) ? p.branchOfService : '';
      const safePType = safeAttendeeParticipantType(p.participantType);
      const safeLinkedin = safeUrl(p.linkedinUrl);
      const safeWebsite = safeUrl(p.websiteUrl);
      const clampedBio = safeBio(p.bio);
      setForm((prev) => ({
        ...prev,
        firstName: (p.firstName as string) || prev.firstName,
        lastName: (p.lastName as string) || prev.lastName,
        participantType: safePType || prev.participantType,
        rank: safeRank || prev.rank,
        branchOfService: safeBranch || prev.branchOfService,
        isRetired: (p.isRetired as boolean) ?? prev.isRetired,
        organization: (p.organization as string) || prev.organization,
        role: (p.role as string) || prev.role,
        linkedinUrl: safeLinkedin || prev.linkedinUrl,
        websiteUrl: safeWebsite || prev.websiteUrl,
        bio: clampedBio || prev.bio,
        accelerationInterests:
          Array.isArray(p.accelerationInterests) && (p.accelerationInterests as string[]).length
            ? (p.accelerationInterests as string[]).slice(0, 5)
            : prev.accelerationInterests,
      }));
    } catch {
      setSignInError('Invalid email or password. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  // ----- Validation -----

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';

    const email = form.email.trim().toLowerCase();
    if (!email) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address';
    }

    if (!form.participantType) errs.participantType = 'Please select a participant type';
    if (!form.organization.trim()) errs.organization = 'Organization is required';
    if (!form.role.trim()) errs.role = 'Job position is required';

    // Validate optional URLs against the same rule the backend enforces.
    // normalizeUrl() prepends https:// but does not guarantee a valid URL, so
    // free-text entries (e.g. a name) must be caught here rather than failing
    // server-side with a generic error.
    if (form.linkedinUrl.trim() && !isValidUrl(normalizeUrl(form.linkedinUrl))) {
      errs.linkedinUrl = 'Enter a valid URL (e.g., https://linkedin.com/in/you) or leave blank';
    }
    if (form.websiteUrl.trim() && !isValidUrl(normalizeUrl(form.websiteUrl))) {
      errs.websiteUrl = 'Enter a valid URL (e.g., https://example.com) or leave blank';
    }

    if (form.bio.length > 300) errs.bio = 'Bio must be 300 characters or fewer';

    if (form.accelerationInterests.length === 0) {
      errs.accelerationInterests = 'Please select at least one technology interest';
    } else if (form.accelerationInterests.length > 5) {
      errs.accelerationInterests = 'Please select no more than 5 technology interests';
    }

    if (form.acknowledgements.length < ATTENDEE_ACKNOWLEDGEMENTS.length) {
      errs.acknowledgements = 'All acknowledgements are required';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ----- Submission -----

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!event) return;

    const email = form.email.trim().toLowerCase();
    const isCurrentUser = isAuthenticated && user?.email?.toLowerCase() === email;

    // Returning attendees must sign in before submitting. The blur-triggered check
    // can be skipped by browser autofill (which fills the email without firing a
    // blur), so re-run it on submit — before field validation — so an existing
    // account routes to the sign-in popup instead of the backend's dead-end
    // "please sign in" error or a wall of validation messages. After signing in,
    // the form pre-fills and the attendee resubmits as the current user.
    if (email && !isCurrentUser) {
      try {
        const check = await api.post<{ success: boolean; data: EmailCheckResult }>(
          '/register/check-email',
          { email, eventId: event.id },
        );
        if (check.success && check.data.exists) {
          setEmailCheckResult(check.data);
          if (check.data.isRejected) { setShowRejected(true); return; }
          if (check.data.hasPendingRegistration) { setShowPendingApproval(true); return; }
          if (check.data.hasEventRegistration) { setShowAlreadyRegistered(true); return; }
          if (check.data.hasAccount) { setShowSignIn(true); return; }
        }
      } catch {
        // Safety net only — if the check fails, fall through to normal validation.
      }
    }

    if (!validate()) return;

    setSubmitting(true);
    try {
      const body = {
        registrationType: 'attendee' as const,
        eventId: event.id,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        participantType: form.participantType,
        rank: form.rank || undefined,
        branchOfService: form.branchOfService || undefined,
        isRetired: form.isRetired,
        organization: form.organization.trim(),
        role: form.role.trim(),
        linkedinUrl: normalizeUrl(form.linkedinUrl) || undefined,
        websiteUrl: normalizeUrl(form.websiteUrl) || undefined,
        bio: form.bio.trim() || undefined,
        accelerationInterests: form.accelerationInterests,
        athenaOptOut: form.athenaOptOut,
        acknowledgements: form.acknowledgements,
      };

      const res = await api.post<{ success: boolean }>('/register', body);
      if (res.success) {
        setSubmitted(true);
      }
    } catch (err: unknown) {
      // Surface the exact field(s) the backend rejected, both inline on the
      // fields and as a summary message, instead of a generic error.
      const message = getApiErrorMessage(err);
      const fieldErrors = getApiFieldErrors(err);
      // If the backend blocked this as a returning attendee needing to sign in,
      // open the popup so there's a path forward instead of a dead-end message.
      if (message === 'Please sign in to register as a returning attendee') {
        setShowSignIn(true);
      }
      setErrors((prev) => ({ ...prev, ...fieldErrors, submit: message }));
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Loading / Error states -----

  if (loadingEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-2xl font-bold">Registration Unavailable</h1>
          <p className="text-muted-foreground">{eventError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <RegistrationConfirmation registrationType="attendee" />
      </div>
    );
  }

  // ----- Render -----

  const pendingRegistration = !!emailCheckResult?.hasPendingRegistration;
  const isRejected = !!emailCheckResult?.isRejected;
  // Email approved for the active event — re-submitting would 409.
  const alreadyRegistered = !!emailCheckResult?.hasEventRegistration && !pendingRegistration && !isRejected;
  const eventLabel = event
    ? `${event.name} (${formatEventDateRange(event.dateStart, event.dateEnd)})`
    : 'this event';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-navy text-white py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-5">
          <img src="/nps-logo.png" alt="NPS Foundation" className="h-14 w-auto shrink-0" />
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3xl font-bold">Converge @ NPS</h1>
            <p className="text-sm text-blue-200">
              {event ? formatEventDateRange(event.dateStart, event.dateEnd) : ''} &middot; General Attendee Registration
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* ---------- Personal Information ---------- */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Personal Information</h2>

            {/* Email — first so pre-fill fires before user types name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                onBlur={handleEmailBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="you@example.com"
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>

            {/* Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  placeholder="First name"
                />
                {errors.firstName && (
                  <p className="text-xs text-destructive">{errors.firstName}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Last Name *</label>
                <Input
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  placeholder="Last name"
                />
                {errors.lastName && (
                  <p className="text-xs text-destructive">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Participant Type */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Participant Type *</label>
              <Select
                value={form.participantType}
                onValueChange={(v) => set('participantType', v)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select participant type" />
                </SelectTrigger>
                <SelectContent>
                  {ATTENDEE_PARTICIPANT_TYPES.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.participantType && (
                <p className="text-xs text-destructive">{errors.participantType}</p>
              )}
            </div>

            {/* Military Fields */}
            <MilitaryFields
              branchOfService={form.branchOfService}
              rank={form.rank}
              isRetired={form.isRetired}
              onBranchChange={(v) => { set('branchOfService', v); set('rank', ''); }}
              onRankChange={(v) => set('rank', v)}
              onRetiredChange={(v) => set('isRetired', v)}
            />
          </div>

          {/* ---------- Organization & Role ---------- */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Organization & Role</h2>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Organization *</label>
              <Input
                value={form.organization}
                onChange={(e) => set('organization', e.target.value)}
                placeholder="Your organization"
              />
              {errors.organization && (
                <p className="text-xs text-destructive">{errors.organization}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Job Position *</label>
              <Input
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                placeholder="Your job title or position"
              />
              {errors.role && <p className="text-xs text-destructive">{errors.role}</p>}
            </div>
          </div>

          {/* ---------- Online Presence ---------- */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Online Presence</h2>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">LinkedIn</label>
              <Input
                type="url"
                value={form.linkedinUrl}
                onChange={(e) => set('linkedinUrl', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
              />
              {errors.linkedinUrl && (
                <p className="text-xs text-destructive">{errors.linkedinUrl}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Personal/Organization Website</label>
              <Input
                type="url"
                value={form.websiteUrl}
                onChange={(e) => set('websiteUrl', e.target.value)}
                placeholder="https://yourwebsite.com"
              />
              {errors.websiteUrl && (
                <p className="text-xs text-destructive">{errors.websiteUrl}</p>
              )}
            </div>
          </div>

          {/* ---------- Bio ---------- */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">About You</h2>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Bio</label>
              <Textarea
                value={form.bio}
                onChange={(e) => set('bio', e.target.value)}
                placeholder="Brief description of your background and interests (optional)"
                rows={4}
                maxLength={300}
              />
              <div className="flex justify-between">
                {errors.bio ? (
                  <p className="text-xs text-destructive">{errors.bio}</p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-muted-foreground">{form.bio.length}/300</p>
              </div>
            </div>
          </div>

          {/* ---------- Technology Interests ---------- */}
          <TechnologyAreaPicker
            selected={form.accelerationInterests}
            onChange={(values) => set('accelerationInterests', values)}
            max={5}
            error={errors.accelerationInterests}
            label="Technology Interests *"
            description="Select up to 5 technology areas you are most interested in"
          />

          {/* ---------- Acknowledgements ---------- */}
          <AcknowledgementSection
            acknowledgements={ATTENDEE_ACKNOWLEDGEMENTS}
            accepted={form.acknowledgements}
            onChange={(values) => set('acknowledgements', values)}
            error={errors.acknowledgements}
          />

          {/* ---------- Athena Opt-Out ---------- */}
          <AthenaOptOutCheckbox
            checked={form.athenaOptOut}
            onChange={(v) => set('athenaOptOut', v)}
          />

          {/* ---------- Submit ---------- */}
          {submitAttempted && Object.keys(errors).filter((k) => k !== 'submit').length > 0 && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              Please fix the highlighted fields above before submitting.
            </div>
          )}
          {errors.submit && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {errors.submit}
            </div>
          )}

          {isRejected ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-center text-destructive">
              Your registration was not approved. Check your email for a resubmit link.
            </div>
          ) : pendingRegistration ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-center text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              Your pre-registration is pending admin approval.
            </div>
          ) : alreadyRegistered ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-center text-muted-foreground">
              You're already registered for this event — no need to submit again.
            </div>
          ) : (
            <Button type="submit" className="w-full h-12 text-base" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Registration'
              )}
            </Button>
          )}

          <p className="text-xs text-muted-foreground text-center">
            Questions? Email{' '}
            <a
              href="mailto:converge@npsfoundation.org"
              className="text-primary underline"
            >
              converge@npsfoundation.org
            </a>
          </p>
        </div>
      </form>

      {/* Sign-in modal for existing accounts */}
      <Dialog open={showSignIn} onOpenChange={(open) => { if (!signingIn) setShowSignIn(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign in to continue</DialogTitle>
            <DialogDescription>
              An account with <strong>{form.email}</strong> exists from previous events. Please sign in here, then continue completing the registration form (double-check all pre-populated fields).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Email</Label>
              <Input value={form.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                value={signInPassword}
                onChange={(e) => setSignInPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSignIn(); }}
                placeholder="Enter your password"
                autoFocus
              />
            </div>
            {signInError && (
              <p className="text-sm text-destructive">{signInError}</p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={handleSignIn} disabled={signingIn} className="w-full">
                {signingIn ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Signing in...</> : 'Sign In'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Forgot your password?{' '}
                <Link to="/forgot-password?redirect=/register/attendee" className="text-primary hover:underline">
                  Click here to reset it
                </Link>
                . Then return to the registration page to continue registering for the event. You must submit the registration form in order to attend the current event.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending-approval modal — registration submitted but awaiting admin review */}
      <Dialog open={showPendingApproval} onOpenChange={setShowPendingApproval}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center dark:bg-amber-900">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-center">Pre-Registration Pending Admin Approval</DialogTitle>
            <DialogDescription className="text-center">
              Your pre-registration for {eventLabel} has been received and is awaiting review. You'll be
              able to access the event once an admin approves your pre-registration.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowPendingApproval(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejected modal — registration was not approved */}
      <Dialog open={showRejected} onOpenChange={setShowRejected}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <DialogTitle className="text-center">Registration Not Approved</DialogTitle>
            <DialogDescription className="text-center">
              Your registration for {eventLabel} was not approved. Check your email for a resubmit link to try again.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowRejected(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Already-registered modal — returning attendee already registered/approved for this event */}
      <Dialog open={showAlreadyRegistered} onOpenChange={setShowAlreadyRegistered}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-1 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-center">You're already registered for this event</DialogTitle>
            <DialogDescription className="text-center">
              Our records show you're already registered for {eventLabel}. You don't need to
              register again — sign in to the app to view or update your details.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Link to="/auth">
              <Button className="w-full">Sign in to the app</Button>
            </Link>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setShowAlreadyRegistered(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
